import React, { useState } from 'react';

import { Box, Button, ButtonProps, Tooltip } from '@mantine/core';
import { IconAlertCircle, IconPhone, IconPhoneEnd } from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { LiveKitRoomWrapper } from 'components/TalkWithAgentButton/LiveKitRoomWrapper';
import { LiveKitVoiceAgentSession } from 'components/TalkWithAgentButton/LiveKitVoiceAgentSession';
import { useSubscriptionContext } from 'contexts/SubscriptionContext';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { LiveKitService } from 'services/livekit-service';
import { NotificationService } from 'services/notification-service';
import { UsageService } from 'services/usage-service';
import { USER_USAGE_QUERY_KEY } from 'shared/constants/query-keys';
import { ROUTE_LINKS } from 'shared/constants/route-links';
import { InlineSmartAssistant, SmartAssistantId } from 'shared/interfaces/livekit/CreateAccessToken';

export interface TalkWithAgentButtonProps extends ButtonProps {
  smartAssistant: InlineSmartAssistant | SmartAssistantId;
  disabledTooltipLabel?: string;
}

export function TalkWithAgentButton({
  smartAssistant,
  disabledTooltipLabel,
  ...buttonProps
}: TalkWithAgentButtonProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [subscription] = useSubscriptionContext();
  const [isSessionLoading, setIsSessionLoading] = useState(false);

  const usageQuery = useQuery({
    queryKey: [USER_USAGE_QUERY_KEY],
    queryFn: () => UsageService.getUsage()
  });

  const createAccessTokenMutation = useMutation({
    onMutate: () => setIsSessionLoading(true),
    mutationFn: async () => {
      return await LiveKitService.createAccessTokenForNewRoom(
        typeof smartAssistant !== 'string'
          ? {
              inlineParticipantMetadata: {
                inlineSmartAssistant: smartAssistant,
                userId: subscription.user,
                organisationId: subscription.organisation
              }
            }
          : {
              persistedParticipantMetadata: {
                smartAssistantId: smartAssistant
              }
            }
      );
    },
    onError: (error: AxiosError) => {
      NotificationService.showResult(false, 'FEED_BACK.SMART_ASSISTANT.FAILURE.TALK_WITH_AGENT', error);
      setIsSessionLoading(false);
    }
  });

  const endSession = () => {
    setIsSessionLoading(false);
    usageQuery.refetch();
    createAccessTokenMutation.reset();
  };

  const usage = usageQuery.data;
  if (!usage) return null;

  const isUsageLimitReached = usage.totalVoiceCallSecondsCount >= subscription.product.totalVoiceCallSecondsLimit;
  const liveKitToken = createAccessTokenMutation.data;
  const isCallActive = !!liveKitToken && !isSessionLoading;

  return (
    <>
      <Tooltip w={250} multiline label={disabledTooltipLabel} disabled={!disabledTooltipLabel}>
        <Box>
          {isUsageLimitReached ? (
            <Tooltip
              label={t('SMART_ASSISTANT.PHONE_AGENT_BASICS_SECTION.TALK_WITH_AGENT_BUTTON.OUT_OF_MINUTES_TOOLTIP')}
              multiline
              width={250}
              position="top"
              withArrow>
              <Button
                onClick={() => navigate(ROUTE_LINKS.billing)}
                variant="light"
                color="orange"
                radius="md"
                leftIcon={<IconAlertCircle />}
                {...buttonProps}>
                {t('SMART_ASSISTANT.PHONE_AGENT_BASICS_SECTION.TALK_WITH_AGENT_BUTTON.OUT_OF_MINUTES')}
              </Button>
            </Tooltip>
          ) : (
            <Button
              onClick={() => (createAccessTokenMutation.data ? endSession() : createAccessTokenMutation.mutate())}
              disabled={!!disabledTooltipLabel}
              loading={createAccessTokenMutation.isLoading || isSessionLoading}
              color={isCallActive ? 'red.5' : 'dark'}
              variant="filled"
              radius="md"
              miw={140}
              leftIcon={isCallActive ? <IconPhoneEnd size={16} /> : <IconPhone size={16} />}
              {...buttonProps}>
              {isCallActive
                ? t('SMART_ASSISTANT.PHONE_AGENT_BASICS_SECTION.TALK_WITH_AGENT_BUTTON.END_CALL')
                : t('SMART_ASSISTANT.PHONE_AGENT_BASICS_SECTION.TALK_WITH_AGENT_BUTTON.TALK_WITH_AGENT')}
            </Button>
          )}
        </Box>
      </Tooltip>

      {liveKitToken && (
        <LiveKitRoomWrapper token={liveKitToken}>
          <LiveKitVoiceAgentSession setIsLoading={setIsSessionLoading} onSessionEnd={endSession} />
        </LiveKitRoomWrapper>
      )}
    </>
  );
}
